/**
 * The portal's data source.
 *
 * Two implementations: {@link createHttpPortalApi}, which the app uses and
 * which reads what machines have reported, and {@link createDemoApi}, which is
 * synthetic and now serves the public `/demo` page rather than anybody's own
 * dashboard.
 *
 * This interface is the whole boundary, and it held: the real implementation
 * landed beside the demo one and no page above it changed. `Overview` and
 * `JobDetail` remain pure functions of {@link PortalOverview}, which is also
 * what lets them be tested against literal fixtures rather than a server.
 */

import type {
  AlertChannelKind,
  NotificationSettings,
  PlanNotificationOverride,
  PortalOverview,
  SummaryCadence,
  WebhookChannelKind,
} from "@bigbear/shared";

/** The channel kinds a test alert can actually be sent on — never email, which
 * costs money per send and has no "try it now" the desktop or portal control. */
export type TestableChannelKind = WebhookChannelKind;

/** The rendered parts of a summary email. `html` is a whole document, meant
 * for a sandboxed iframe rather than to be spliced into the page. */
export type SummaryPreview = { subject: string; text: string; html: string };

export interface PortalApi {
  /** Everything the overview needs, in one round trip. */
  overview(): Promise<PortalOverview>;

  /**
   * Forget a machine: its reported jobs and history leave the dashboard, and
   * its device token stops working, so it would need to sign in again to
   * resume reporting.
   *
   * A machine that is still installed corrects itself on its own next report
   * — see `docs/backup-history.md` §5.3 — so this exists for the other case:
   * a reinstalled or retired machine that will never report again, and whose
   * stale entry has no report left to clear it.
   */
  removeMachine(machineId: string): Promise<void>;

  /**
   * Clear one backup plan's run history, without touching the plan itself.
   *
   * A plan that is still installed and reporting repopulates its history on
   * its next run — see `docs/backup-history.md` §5.3 — which is expected:
   * this is a portal-side cleanup, not a way to stop a plan from being
   * tracked. `machineId` is only needed by the synthetic demo source, which
   * has no server to disambiguate a job id for it; the real implementation
   * ignores it, since a plan id is already unique per account.
   */
  clearJobHistory(machineId: string, jobId: string): Promise<void>;

  /** The account's global alert settings — email, Slack, Discord, webhook, and
   * the staleness grace the server checks for on its own hourly cron. */
  notificationSettings(): Promise<NotificationSettings>;

  /**
   * Replace the global alert settings wholesale.
   *
   * Returns the settings the source actually kept — normalisation (clamped
   * intervals, a trimmed webhook URL) happens server-side, so the caller
   * re-renders from what was saved rather than what it sent.
   */
  saveNotificationSettings(
    settings: NotificationSettings,
  ): Promise<NotificationSettings>;

  /** A plan's alert override, or `null` when it has none and inherits the
   * global settings in full. `planId` is a backup plan's id, unique per
   * account — the same id `JobDetail` reaches this page through. */
  planNotificationOverride(
    planId: string,
  ): Promise<PlanNotificationOverride | null>;

  /**
   * Replace one plan's override.
   *
   * An override that is all-null is equivalent to having none, so saving one
   * and clearing one converge: both leave nothing stored for this plan, and
   * this always resolves with a concrete override (never `null`) so the form
   * has a value to redraw from either way.
   */
  savePlanNotificationOverride(
    planId: string,
    override: PlanNotificationOverride,
  ): Promise<PlanNotificationOverride>;

  /** Delete a plan's override outright, so it goes back to inheriting the
   * global settings in full. */
  clearPlanNotificationOverride(planId: string): Promise<void>;

  /**
   * Fire a one-off message at a webhook channel right now, so someone can
   * confirm the URL actually works before trusting it to page them. Rejects
   * with the server's own explanation on failure — a bad URL, a webhook that
   * answered with an error — rather than a generic "could not send".
   */
  sendNotificationTest(
    channel: TestableChannelKind,
    webhookUrl: string,
  ): Promise<void>;

  /**
   * The summary email for the period that has most recently finished,
   * rendered but not sent, so someone can see what a cadence actually gets
   * them before choosing it. Costs nothing on either side, so the page is
   * free to ask again whenever the cadence changes.
   */
  summaryPreview(
    cadence: Exclude<SummaryCadence, "off">,
  ): Promise<SummaryPreview>;

  /**
   * Send a real summary to a channel **as it is currently saved** — so unlike
   * {@link sendNotificationTest} there is no URL to pass, and email is
   * allowed. Rejects with the server's own message, including the one that
   * says a test email has already gone out today.
   */
  sendSummaryTest(
    channel: AlertChannelKind,
    cadence: Exclude<SummaryCadence, "off">,
  ): Promise<void>;

  /** Whether this source is synthetic, so the UI can say so. */
  readonly isDemo: boolean;
}

/** Thrown when a data source cannot answer. */
export class PortalError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PortalError";
  }
}
